export const RESUME_CONNECTIONS_EVENT = "haechie:resume-connections";

const CONNECT_GAP_MS = 1200;
const lastConnectAt = new WeakMap();

/** Update session connection type only when it actually changes (avoids sidebar re-render storms). */
export function publishConnectionType(text) {
  const next = String(text || "");
  if (sessionStorage.getItem("connectionType") === next) return false;
  sessionStorage.setItem("connectionType", next);
  window.dispatchEvent(new Event("storageUpdated"));
  return true;
}

/** Keep only the latest WS payload and apply it on a short delay so UI clicks stay responsive. */
export function createLatestMessagePump(onApply, delayMs = 250) {
  let timer = 0;
  let latest = null;
  return {
    push(value) {
      latest = value;
      if (timer) return;
      timer = window.setTimeout(() => {
        timer = 0;
        const next = latest;
        latest = null;
        if (next != null) onApply(next);
      }, delayMs);
    },
    dispose() {
      if (timer) window.clearTimeout(timer);
      timer = 0;
      latest = null;
    },
  };
}

export function dispatchResumeConnections(detail = {}) {
  try {
    window.dispatchEvent(new CustomEvent(RESUME_CONNECTIONS_EVENT, { detail }));
  } catch {
    /* ignore */
  }
}

export function subscribeResumeConnections(handler) {
  window.addEventListener(RESUME_CONNECTIONS_EVENT, handler);
  return () => window.removeEventListener(RESUME_CONNECTIONS_EVENT, handler);
}

function safeConnect(connectFn) {
  const now = Date.now();
  const prev = lastConnectAt.get(connectFn) || 0;
  if (now - prev < CONNECT_GAP_MS) return;
  lastConnectAt.set(connectFn, now);
  connectFn();
}

function closeQuietly(ws) {
  if (!ws) return;
  try {
    ws.onclose = null;
    ws.onerror = null;
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      ws.close();
    }
  } catch {
    /* ignore */
  }
}

/**
 * Factory-safe socket resume (no page reload, no backend ping).
 * - Dead/closed → reconnect now
 * - Still connecting/closing → leave it (avoid duplicate sockets)
 * - Open + force (sleep / unlock / long away / network back) → replace socket
 * - Open otherwise → do nothing
 */
export function resumeWebSocketIfNeeded(ws, connectFn, options = {}) {
  if (typeof connectFn !== "function") return;

  const force = Boolean(options.force);
  const clearPendingRetry =
    typeof options.clearPendingRetry === "function"
      ? options.clearPendingRetry
      : () => {};

  try {
    const state = ws ? ws.readyState : WebSocket.CLOSED;

    if (state === WebSocket.CONNECTING || state === WebSocket.CLOSING) {
      return;
    }

    if (state === WebSocket.OPEN) {
      if (!force) return;
      closeQuietly(ws);
      clearPendingRetry();
      safeConnect(connectFn);
      return;
    }

    clearPendingRetry();
    safeConnect(connectFn);
  } catch {
    const state = ws ? ws.readyState : WebSocket.CLOSED;
    if (state !== WebSocket.OPEN && state !== WebSocket.CONNECTING) {
      clearPendingRetry();
      safeConnect(connectFn);
    }
  }
}
